// material-ui
import {
    Box,
    Button,
    Grid,
    Paper,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useForgetPasswordMutation } from "../../store/slices/apiSlice";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
// import logo from "../../assets/images/logo/bitmap@2x.jpg";
import logo from "../../assets/images/logo/sga-takeda.png";
import virLogo from "../../assets/images/logo/vir-logo.jpg";
import CustomButton from "../../components/@extended/CustomButton";
import * as Yup from "yup";
import { EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE, extractErrorMessage } from "../../util/util";
import mevoice from "../../assets/images/login/ME-VOICE_Logo.png";
import { useFormik } from "formik";
import ShowMessage from "../../components/showMessage/ShowMessage";
import { useResponsive } from "../../hooks/ResponsiveProvider";
import { useDispatch } from "react-redux";
import { openMessage } from "../../store/slices/showMessageSlice";

import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/pagination';

const Reset=()=>{
    const [forgot, { error }] = useForgetPasswordMutation();
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isSmallScreen, isMediumScreen } = useResponsive();

    const docSchema = Yup.object().shape({
      email: Yup.string().required("Username/Email Id is required")
      .matches(EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE),
    });

    const formik = useFormik({
        initialValues: {
          email: "",
        },
        validationSchema: docSchema,
        onSubmit: async (values) => {
          try {
            const result = await forgot({
              email: values.email,
            });
            if (!result?.error) {
              openMessageNotification({
                message: "Password reset email sent successfully. Please check your inbox for the temporary password.",
                type: "success",
              });
              navigate("/login");
            } else {
              // Extract user-friendly error message
              const errorMessage = extractErrorMessage(result.error, {
                defaultMessage: "Failed to send password reset email. Please try again.",
                customMessages: {
                  USER_NOT_FOUND: "User not found. Please check your email address and try again.",
                  SERVER_ERROR: "Unable to process password reset request. Please try again later.",
                }
              });
              openMessageNotification({
                message: errorMessage,
                type: "error",
              });
            }
          } catch (error) {
            // Handle unexpected errors
            const errorMessage = extractErrorMessage(error, {
              defaultMessage: "An unexpected error occurred. Please try again.",
            });
            openMessageNotification({
              message: errorMessage,
              type: "error",
            });
          }
        },
      });

      const handleFormSubmit = (event) => {
        event.preventDefault(); // Prevents the default form submission behavior
        formik.handleSubmit();
      };
    
      const openMessageNotification = (message) => {
        dispatch(openMessage({ message: message.message, messageSeverity: message.type }))
      };
    
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 2, sm: 3, md: 4 }, pt: { xs: 4, sm: 6, md: isMediumScreen ? 8 : 4 } }}>
          <Typography variant="h6" color="initial" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, textAlign: 'center', px: 1 }}>
            Forgot Password
          </Typography>
        </Box>
        <form onSubmit={handleFormSubmit}>
          <Grid container rowSpacing={3}>
            <Grid item xs={12} px={{ xs: 2, sm: 3 }}>
              <Typography variant="body1" color="#5d5d5d" pb={1}>
                Username/Email Id
              </Typography>
              <TextField
                name="email"
                fullWidth
                placeholder="Username/Email Id"
                onChange={formik.handleChange}
                value={formik.values.email}
                onBlur={formik.handleBlur}
                helperText={formik.touched.email && formik.errors.email}
                error={formik.touched.email && Boolean(formik.errors.email)}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 0 },
                px: { xs: 2, sm: 3 },
              }}
            >
              <Button
                variant="text"
                sx={{ width: { xs: '100%', sm: 'fit-content' }, color: theme.palette.primary.light, p: { xs: 0, sm: 0 } }}
                size={isSmallScreen ? 'medium' : 'large'}
                onClick={() => {
                  navigate('/login');
                }}
              >
                Back to Login
              </Button>

              <CustomButton
                size={isSmallScreen ? 'medium' : 'large'}
                type="submit"
                fullWidth={isSmallScreen}
                sx={{
                  width: { xs: '100%', sm: 150 },
                  backgroundColor: theme.palette.primary.light,
                }}
              >
                Submit
              </CustomButton>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Box component="img" src={virLogo} alt="Bloqcube" sx={{ width: { xs: 80, sm: 100 }, height: 'auto' }} />
              </Box>
            </Grid>
          </Grid>
        </form>
      </>
    );
}

const ForgotPassword = () => {
  
  const theme = useTheme();
  const { isSmallScreen, isMediumScreen } = useResponsive();
  
  // const openMessageNotification = (message) => {
  //   dispatch(openMessage({ message: message.message, messageSeverity: message.type }))
  // };

  return (
    <>
      <Grid
        container
        sx={{
          minHeight: '100vh',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'auto',
          px: { xs: 1.5, sm: 2, md: 0 },
          pt: { xs: 7, sm: 8, md: 0 },
          pb: { xs: 2, md: 0 },
        }}
      >
          <Box
            sx={{
              p: { xs: 1.5, sm: 2, md: 4 },
              position: 'absolute',
              top: { xs: 1, sm: 2, md: 2 },
              left: isMediumScreen ? 0 : 'auto',
              right: 0,
              width: isMediumScreen ? '100%' : 'auto',
              display: 'flex',
              justifyContent: isMediumScreen ? 'center' : 'flex-end',
              boxSizing: 'border-box',
            }}
          >
            <Button
              size={isSmallScreen ? 'medium' : 'large'}
              variant="outlined"
              sx={{ textTransform: 'none', color: theme.palette.primary.light }}
              startIcon={
                <HelpOutlineOutlinedIcon
                  sx={{ color: theme.palette.primary.light }}
                />
              }
            >
              Help & Support
            </Button>
          </Box>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMediumScreen ? 'center' : 'flex-end',
          }}
        >
          <Paper
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', md: 480 },
              height: isMediumScreen ? 'auto' : 550,
              boxSizing: 'border-box',
              mx: 'auto',
            }}
            elevation={isMediumScreen ? 0 : 5}
          >

            <Box sx={{ display: 'flex', justifyContent: 'center', pt: { xs: 3, sm: 4, md: 5 }, px: { xs: 1, sm: 0 } }}>
              <Box
                component="img"
                src={logo}
                alt="Bloqcube"
                sx={{ width: '100%', maxWidth: { xs: 220, sm: 250 }, height: 'auto' }}
              />
            </Box>

            <Reset/>

          </Paper>
        </Grid>
        {!isMediumScreen && (
          <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: { md: 550 } }}>
            <Box sx={{ width: '100%', maxWidth: 480, ml: { md: 2, lg: 3 }, flexShrink: 0 }}>
              <Swiper style={{ width: '100%', height: 480, maxWidth: '100%' }}>
                <SwiperSlide>
                  <img src={mevoice} alt="Bloqcube" width="100%" height={'100%'} style={{ borderRadius: 10 }} />
                </SwiperSlide>
              </Swiper>
            </Box>
          </Grid>
        )}
      </Grid>
      <ShowMessage />
    </>
  );
};
  
export default ForgotPassword;
  